import { Skeleton } from "@mui/material";
import { LazyResizeableCodeEditor as ResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import {
    EMPTY_OBJECT_PATH,
    getDefaultRequiredValue,
} from "components/Form/components/SchemaForm/SchemaFormService";
import { SchemaFormFragment } from "components/Form/components/SchemaForm/fragments/SchemaFormFragment";
import { normalizeSchemaForForm } from "components/Form/components/SchemaForm/SchemaFormInference";
import {
    SchemaFormArrayItemIdProvider,
    useSchemaFormArrayItemIdRegistry,
} from "components/Form/components/SchemaForm/SchemaFormArrayItemIds";
import { SchemaFormProps } from "components/Form/components/SchemaForm/SchemaForm.types";
import { Toggle } from "components/Form/components/Toggle/Toggle";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { getDocumentationUrl } from "containers/Documentation/documentationService";
import {
    blurActiveElement,
    isMonacoEditorActiveElement,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { isEqual, isObject } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RouterContext } from "utils/RouterManager/RouterManager";
import { parseYaml, serializeToPrettyYaml } from "utils/valueTransformers";
import "./SchemaForm.scss";

export type {
    ObjectPath,
    SchemaFormErrorData,
    SchemaFormProps,
    SchemaFormSchema,
    SchematizedValues,
} from "components/Form/components/SchemaForm/SchemaForm.types";

export { SchemaFormRenderErrors } from "components/Form/components/SchemaForm/SchemaForm.constants";

const TestForm = ({
    formSchema,
    attributes,
}: {
    formSchema: any;
    attributes: { isNewRecord?: boolean };
}) => {
    const values = useMemo(
        () => getDefaultRequiredValue(formSchema, formSchema, null),
        [formSchema]
    );
    const inputProps = useMemo(
        () => ({ value: { values, schema: formSchema } }),
        [formSchema, values]
    );
    const formAttributes = useMemo(
        () => ({ ...(attributes ?? {}), canUpdateFieldOnSchemaChanges: true }),
        [attributes]
    );
    return (
        <div className="testFormWrapper">
            <div className="testFormHeader">Preview</div>
            <SchemaForm inputProps={inputProps} attributes={formAttributes} />
        </div>
    );
};

/**
 * SchemaForm Component
 *
 * A dual-mode custom field component for react-final-form that allows users to edit structured data
 * either through a visual form (generated from a schema) or a raw YAML code editor.
 *
 * This is intended to be used as a field inside a `react-final-form` form.
 *
 * Props follow the `FieldRenderProps` interface with additional metadata under `attributes`.
 *
 * Designed for advanced use cases like configuring connector records, wrappers, or other resources
 * that support schema-based configuration.
 *
 * Features:
 * - Automatic YAML serialization/parsing
 * - Real-time form/code sync
 * - Validation and deprecation handling
 * - Test preview button
 *
 * @param {SchemaFormProps} props - Component props
 * @param {Object} props.inputProps - Input properties including value, onChange, onBlur, etc.
 * @param {Object} props.inputProps.value - Field state, containing both `values` and `schema`
 * @param {Object} props.attributes - Additional attributes for field behavior
 * @param {boolean} props.isTestButtonShown - Whether to show the test form button
 * @param {Object} props.errorData - Error information to display
 * @returns {JSX.Element} The rendered schema form component
 */
export const SchemaForm = ({
    inputProps,
    attributes,
    isTestButtonShown,
    errorData,
    additionalCodeEditorRootProps,
}: SchemaFormProps) => {
    /**
     * Controls whether the form view is enabled (vs code editor view).
     * Disabled when there's no schema, deprecations, or invalid code editor content.
     *
     * @type {boolean}
     * @default true
     */
    const [schemaFormEnabled, setSchemaFormEnabled] = useState(true);

    /**
     * The schema definition for the form, extracted from input props.
     * Used to generate the visual form structure.
     *
     * @type {Object}
     */
    // Enrich a plain JSON Schema (no `element` keys — how flow input_schemas and
    // MCP tool inputSchemas are written) into the form-element dialect so it
    // renders as a real form instead of "Unknown form element". A schema that is
    // already the dialect (or empty) is returned unchanged. Only the schema used
    // to RENDER the form is normalized; the raw schema is kept for onChange/code
    // editor so nothing normalized is ever persisted back.
    const formSchema = useMemo(
        () => normalizeSchemaForForm(inputProps.value.schema),
        [inputProps.value.schema]
    );
    const inputSchema = inputProps.value.schema;

    /**
     * Whether the schema is still being fetched asynchronously (currently only
     * set for custom objects, whose object_template schema arrives via the
     * websocket full-state push after the record screen mounts). While true we
     * render a placeholder rather than falling back to the raw code editor,
     * which prevents the codeview-then-form "flash" on custom-object open.
     */
    const isSchemaLoading = Boolean(inputProps.value.isSchemaLoading);
    const onFieldChange = inputProps.onChange;

    /**
     * Propagates form edits to react-final-form without mutating input props or
     * marking the field touched on every keystroke.
     */
    const handleSetData = useCallback(
        (values) => {
            setFormValues(values);
            onFieldChange?.({
                values,
                schema: inputSchema,
            });
        },
        [inputSchema, onFieldChange]
    );

    /**
     * Internal state managing form values and code editor content.
     * Keeps both the structured form data and YAML string representation in sync.
     *
     * @type {Object}
     * @property {any} formValues - The structured form data
     * @property {string} codeEditorContent - The YAML string representation
     */
    const [state, setState] = useState({
        formValues: inputProps.value.values,
        codeEditorContent: serializeToPrettyYaml(inputProps.value.values),
    });

    /**
     * Updates form values and synchronizes the code editor content.
     * Ensures both the structured data and YAML representation stay in sync.
     *
     * @param {any} values - The new form values to set
     */
    const setFormValues = (values) => {
        setState((prev) => ({
            ...prev,
            formValues: values,
            codeEditorContent: serializeToPrettyYaml(values), // Keep values in sync
        }));
    };

    /**
     * Updates only the code editor content without parsing.
     * Used when user is typing in the code editor to avoid premature parsing.
     *
     * @param {string} content - The new YAML content
     */
    const setCodeEditorContent = (content) => {
        setState((prev) => ({
            ...prev,
            codeEditorContent: content,
        }));
    };

    /**
     * Author-time "Enrich from JSON Schema" action, shown only when this
     * SchemaForm is being used as a schema BUILDER (its own data is itself a
     * JSON Schema — the input_schema / output_schema editors set
     * `attributes.enrichSchemaButton`). Rewrites the current schema into the
     * form-element dialect via {@link normalizeSchemaForForm} and persists it
     * back through onChange, so the builder then exposes the rich dialect
     * controls (labels, dropdowns, optional/const, ...) instead of resolving a
     * plain JSON-Schema node to "Invalid choice".
     *
     * Idempotent: a schema that is already the dialect (or empty / non-object)
     * is returned unchanged by the normalizer, so a no-op enrich neither mutates
     * the value nor flags the field dirty.
     */
    const handleEnrichSchema = useCallback(() => {
        const current = state.formValues;
        const enriched = normalizeSchemaForForm(current);
        if (isEqual(enriched, current)) {
            return;
        }
        setFormValues(enriched);
        onFieldChange?.({ values: enriched, schema: inputSchema });
        attributes?.enableSaveManually?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.formValues, onFieldChange, inputSchema, attributes]);

    const ajv = useContext(RouterContext).ajv;
    const arrayItemIdRegistry = useSchemaFormArrayItemIdRegistry();

    /**
     * Synchronizes form values when input becomes read-only.
     * Ensures the form reflects the current input values.
     */
    useEffect(() => {
        if (inputProps?.isReadOnly) {
            setFormValues(inputProps.value.values);
        }
    }, [inputProps?.isReadOnly, inputProps.value.values]);

    /**
     * Initializes form values when they become available.
     * Handles the case where defaultValues are not passed on mount.
     */
    useEffect(() => {
        // defaultValues are not passed onMount, we should pass them later
        if (state.formValues === undefined && inputProps.value.values) {
            setFormValues(inputProps.value.values);
        }
    }, [state.formValues, inputProps.value.values]);

    /**
     * Handles dirty state synchronization between form and code editor.
     * When the form is dirty and code editor is active, blurs it.
     * Otherwise, resets the form values and clears dirty state.
     */
    useEffect(() => {
        if (attributes?.hasTemporarySchemaFormValues) {
            setFormValues(inputProps.value.values);
            attributes?.setHasTemporarySchemaFormValues(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputProps.value.values, attributes?.hasTemporarySchemaFormValues]);

    useEffect(() => {
        if (attributes?.isDirty) {
            if (isMonacoEditorActiveElement()) {
                blurActiveElement();
            } else {
                setFormValues(inputProps.value.values);

                attributes?.resetDirty?.();
            }
        }
    }, [attributes, inputProps.value.values]);

    /**
     * Explicit "discard my edits, show server state" on Refresh.
     *
     * The record screen's onRefresh() calls react-final-form's form.restart() to reset the
     * RFF-driven fields to the latest server values, but this SchemaForm self-manages its
     * editor state and doesn't follow the RFF value stream, so it would otherwise keep
     * showing the user's local edits. When the record screen bumps `refreshNonce`, re-read
     * the (now server-state) input values into the editor. Skips the initial mount (nonce 0).
     */
    const refreshNonce = attributes?.refreshNonce;
    useEffect(() => {
        if (refreshNonce) {
            setFormValues(inputProps.value.values);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshNonce]);

    /**
     * Determines if there's no schema available for form generation.
     * Form view is disabled when schema is empty or missing.
     *
     * @type {boolean}
     */
    const noSchema = useMemo(
        () => isObject(formSchema) && Object.keys(formSchema).length === 0,
        [formSchema]
    );

    /**
     * Checks if the current schema has deprecation warnings.
     * Form view is disabled when deprecations are present.
     *
     * @type {boolean}
     */
    const hasDeprecation = useMemo(() => attributes?.hasDeprecation, [attributes?.hasDeprecation]);

    /**
     * Tracks whether the code editor content is valid YAML.
     * Form view is disabled when code editor has syntax errors.
     *
     * @type {boolean}
     * @default true
     */
    const [isCodeEditorValid, setIsCodeEditorValid] = useState(true);

    /**
     * Determines if the form view should be disabled.
     * Disabled when there's no schema, deprecations, or invalid code editor content.
     *
     * @type {boolean}
     */
    const isFormViewDisabled = useMemo(
        () => noSchema || hasDeprecation || !isCodeEditorValid,
        [hasDeprecation, isCodeEditorValid, noSchema]
    );

    /**
     * Updates schema form enabled state based on form view availability.
     * Automatically switches to code editor when form view is disabled.
     */
    useEffect(() => {
        if (isFormViewDisabled || formSchema == null) {
            setSchemaFormEnabled(false);
        } else {
            setSchemaFormEnabled(true);
        }
    }, [formSchema, isFormViewDisabled]);

    /**
     * Updates form values when schema changes and field updates are allowed.
     * Used for resources that should update their form data when schema changes.
     */
    useEffect(() => {
        if (attributes?.canUpdateFieldOnSchemaChanges) {
            setFormValues(inputProps.value.values);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formSchema]);

    /**
     * The code editor component with YAML parsing and validation.
     * Handles real-time content updates and validation on blur.
     *
     * @type {JSX.Element}
     */
    const codeEditor = useMemo(
        () => (
            <ResizeableCodeEditor
                input={{
                    name: "schemaFormCodeEditor",
                    value: state.codeEditorContent,
                    onChange: (value) => {
                        attributes?.enableSaveManually?.(true);
                        setCodeEditorContent(value);
                    },
                    onBlur: (event) => {
                        try {
                            const values = parseYaml(state.codeEditorContent);
                            const newValue = {
                                values: values,
                                schema: inputProps.value.schema,
                            };
                            setFormValues(values);
                            if (inputProps.onChange) inputProps.onChange(newValue);
                            setIsCodeEditorValid(true);
                        } catch (error: any) {
                            console.error(error);
                            const newValue = {
                                values: state.codeEditorContent,
                                schema: inputProps.value.schema,
                            };
                            // to trigger validation should call inputProps.onChange
                            if (inputProps.onChange) inputProps.onChange(newValue);
                            setIsCodeEditorValid(false);
                        }
                        if (inputProps.onBlur) inputProps.onBlur(event);
                        attributes?.enableSaveManually?.(false);
                    },
                    onFocus: inputProps.onFocus ?? (() => {}),
                    isReadOnly: inputProps.isReadOnly,
                }}
                fieldAttr={attributes}
                meta={{ error: errorData?.messageWithPath, touched: true }}
                fieldProps={{
                    isReadOnly: inputProps.isReadOnly,
                    lang: "yaml",
                    // Read-only structured value (e.g. execution output_value /
                    // input_value with no schema): let CodeEditor own the JSON⇄YAML
                    // display toggle. Editable code-view keeps the plain YAML editor
                    // (its per-keystroke onChange would fight a live re-conversion).
                    structuredDataView: inputProps.isReadOnly,
                    isNewRecord: attributes?.isNewRecord,
                }}
            />
        ),
        [attributes, state.codeEditorContent, errorData?.messageWithPath, inputProps]
    );

    /**
     * Toggle component for switching between form view and code editor.
     * Shows appropriate tooltips when form view is disabled.
     *
     * @type {JSX.Element}
     */
    const RawEditModeToggle = useMemo(() => {
        const onChangeValue = (_, selectedValue) => {
            if (selectedValue !== null) {
                setSchemaFormEnabled(selectedValue);
            }
        };

        const overlay = (
            <div>
                Cannot show form as no schema is provided. Please consult the{" "}
                <a
                    href={getDocumentationUrl(
                        "/docs/schema-creation-and-validation#input-and-output-schema"
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    documentation
                </a>{" "}
                on how to set up a schema.
            </div>
        );

        const deprecationOverlay = hasDeprecation ? (
            <div>
                Form view is disabled because the connector has deprecations. Migrate deprecations
                to enable editing in form view.
            </div>
        ) : null;

        const invalidCodeEditorOverlay = isCodeEditorValid ? null : (
            <div>The code editor is invalid. Please fix the errors and try again.</div>
        );

        const toggle = (
            <Toggle
                options={[
                    { value: true, label: "Form view", icon: IconCollection.formTextbox },
                    { value: false, label: "Code view", icon: IconCollection.codeJson },
                ]}
                value={schemaFormEnabled}
                identifier="rawEditMode"
                onChange={onChangeValue}
                attributes={{ disabled: isFormViewDisabled }}
            />
        );

        return (
            <div
                className={`plainEditorToggleButton ${
                    isFormViewDisabled ? "plainEditorToggleButtonDisabled" : ""
                }`}
            >
                {isFormViewDisabled ? (
                    <Tooltip
                        overlay={deprecationOverlay ?? invalidCodeEditorOverlay ?? overlay}
                        overlayClassName="plainEditorToggleTooltip"
                    >
                        {toggle}
                    </Tooltip>
                ) : (
                    toggle
                )}
            </div>
        );
    }, [hasDeprecation, isCodeEditorValid, isFormViewDisabled, schemaFormEnabled]);

    /**
     * "Enrich from JSON Schema" builder action. Only rendered when the host field
     * opts in via `attributes.enrichSchemaButton` (the schema editors), and only
     * while the field is editable.
     *
     * @type {JSX.Element | null}
     */
    const EnrichButton = useMemo(() => {
        if (!attributes?.enrichSchemaButton || inputProps.isReadOnly || attributes?.disabled) {
            return null;
        }
        return (
            <Tooltip
                overlay="Rewrite this JSON Schema into the Cloudomation form-element dialect, so the builder shows the rich controls (labels, dropdowns, optional/const, ordering). Idempotent — a schema that is already the dialect is left unchanged."
                overlayClassName="plainEditorToggleTooltip"
            >
                <Button
                    label="Enrich from JSON Schema"
                    icon={IconCollection.autoFix}
                    variation="clear"
                    onClick={handleEnrichSchema}
                    className="schemaFormEnrichButton"
                />
            </Tooltip>
        );
    }, [attributes?.enrichSchemaButton, attributes?.disabled, inputProps.isReadOnly, handleEnrichSchema]);

    /**
     * True when this SchemaForm is a schema BUILDER (input_schema / output_schema
     * editor, `attributes.enrichSchemaButton`) that is editable — the only context
     * in which its own data (`state.formValues`) is itself a JSON Schema and the
     * enrich / dialect-mismatch affordances make sense.
     */
    const isEditableSchemaBuilder = useMemo(
        () =>
            Boolean(attributes?.enrichSchemaButton) &&
            !inputProps.isReadOnly &&
            !attributes?.disabled,
        [attributes?.enrichSchemaButton, attributes?.disabled, inputProps.isReadOnly]
    );

    /**
     * Whether the currently-authored schema is a plain JSON Schema that does NOT
     * use the form-element dialect — i.e. enriching it would change it. This is
     * exactly the delta the "Enrich from JSON Schema" action repairs, so the
     * warning and the action stay consistent by construction (the warning shows
     * iff clicking Enrich would do something). Only evaluated in the schema
     * builder; an empty / non-object schema never warns.
     *
     * @type {boolean}
     */
    const needsDialectEnrichment = useMemo(() => {
        if (!isEditableSchemaBuilder) {
            return false;
        }
        const current = state.formValues;
        if (!isObject(current) || Object.keys(current as object).length === 0) {
            return false;
        }
        return !isEqual(normalizeSchemaForForm(current), current);
    }, [isEditableSchemaBuilder, state.formValues]);

    /**
     * Builder-only banner shown when the authored schema is plain JSON Schema
     * (not the form dialect). Points the user at the Enrich action, which the
     * builder header renders alongside the mode toggle.
     *
     * @type {JSX.Element | null}
     */
    const DialectMismatchWarning = useMemo(() => {
        if (!needsDialectEnrichment) {
            return null;
        }
        return (
            <div
                className="schemaFormDialectWarning"
                role="status"
                data-testid="schemaFormDialectWarning"
            >
                This schema is written as plain JSON Schema and does not use the Cloudomation
                form dialect, so the builder cannot show its rich controls (labels, descriptions,
                dropdowns, optional/const, ordering). Use &ldquo;Enrich from JSON Schema&rdquo; to
                convert it.
            </div>
        );
    }, [needsDialectEnrichment]);

    /**
     * Header component containing the form/code editor toggle, the (optional)
     * Enrich action, and the (optional) dialect-mismatch warning below them.
     *
     * @type {JSX.Element}
     */
    const SchemaFormHeader = useMemo(
        () => (
            <>
                <div className="schemaFormHeader">
                    {RawEditModeToggle}
                    {EnrichButton}
                </div>
                {DialectMismatchWarning}
            </>
        ),
        [RawEditModeToggle, EnrichButton, DialectMismatchWarning]
    );

    /**
     * Form view content (schema-driven editor).
     */
    const formView = useMemo(
        () => (
            <>
                <div className="schemaWithHeader">
                    {SchemaFormHeader}
                    <div className="schemaFormComponent">
                        <SchemaFormArrayItemIdProvider value={arrayItemIdRegistry}>
                            <SchemaFormFragment
                                isRoot={true}
                                isReadOnly={inputProps.isReadOnly ?? false}
                                isRecordDisabled={attributes?.disabled || inputProps?.isReadOnly}
                                schema={formSchema}
                                schemaPath={EMPTY_OBJECT_PATH}
                                formData={state.formValues}
                                dataPath={EMPTY_OBJECT_PATH}
                                setData={handleSetData}
                                showLabel
                                errorData={errorData}
                                isNewRecord={attributes?.isNewRecord}
                                ajv={ajv}
                                additionalCodeEditorRootProps={additionalCodeEditorRootProps}
                                omitRequired={attributes?.omitRequired}
                            />
                        </SchemaFormArrayItemIdProvider>
                    </div>
                </div>
                {isTestButtonShown && (
                    <TestForm
                        formSchema={state.formValues}
                        attributes={{ isNewRecord: attributes?.isNewRecord }}
                    />
                )}
            </>
        ),
        [
            SchemaFormHeader,
            inputProps.isReadOnly,
            attributes?.disabled,
            attributes?.isNewRecord,
            attributes?.omitRequired,
            formSchema,
            state.formValues,
            errorData,
            ajv,
            additionalCodeEditorRootProps,
            isTestButtonShown,
            handleSetData,
            arrayItemIdRegistry,
        ]
    );

    return (
        <div className={`SchemaForm ${isTestButtonShown ? "schemaFormWithPreview" : ""}`}>
            {isSchemaLoading ? (
                <div className="schemaFormLoadingPlaceholder" data-testid="schemaFormLoading">
                    <Skeleton variant="text" width="30%" height={28} />
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="text" width="30%" height={28} />
                    <Skeleton variant="rounded" height={40} />
                </div>
            ) : schemaFormEnabled ? (
                formView
            ) : (
                <>
                    <div className="schemaWithHeader">
                        {SchemaFormHeader}
                        <div className="schemaFormComponent">{codeEditor}</div>
                    </div>
                    {isTestButtonShown && (
                        <TestForm
                            formSchema={state.formValues}
                            attributes={{ isNewRecord: attributes?.isNewRecord }}
                        />
                    )}
                </>
            )}
        </div>
    );
};
